import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { money } from "@/lib/calc";
import { Activity, Users, Package, DollarSign, Settings as Cog, Search, Plus, Save, X, Pencil, Power, Trash2 } from "lucide-react";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function Admin() {
  const { user } = useAuth();
  const isSuper = user?.role === "superadmin";
  return (
    <div className="p-4 md:p-6 space-y-4">
      <div><h1 className="font-heading font-extrabold text-3xl tracking-tight text-slate-800">Admin</h1><p className="text-slate-500 text-sm">G&L One configuration & audit</p></div>
      <Tabs defaultValue="activity">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="activity" data-testid="tab-activity" className="gap-1.5"><Activity size={15} strokeWidth={2.5} /> Activity</TabsTrigger>
          <TabsTrigger value="accounts" data-testid="tab-accounts" className="gap-1.5"><Users size={15} strokeWidth={2.5} /> Accounts</TabsTrigger>
          <TabsTrigger value="roster" data-testid="tab-roster" className="gap-1.5"><Package size={15} strokeWidth={2.5} /> Roster</TabsTrigger>
          {(isSuper || user?.role === "admin") && <TabsTrigger value="pricing" data-testid="tab-pricing" className="gap-1.5"><DollarSign size={15} strokeWidth={2.5} /> Pricing</TabsTrigger>}
          <TabsTrigger value="settings" data-testid="tab-settings" className="gap-1.5"><Cog size={15} strokeWidth={2.5} /> Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="activity"><ActivityFeed /></TabsContent>
        <TabsContent value="accounts"><Accounts /></TabsContent>
        <TabsContent value="roster"><Roster /></TabsContent>
        {(isSuper || user?.role === "admin") && <TabsContent value="pricing"><Pricing /></TabsContent>}
        <TabsContent value="settings"><SettingsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function ActivityFeed() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/audit").then((r) => setItems(r.data)); }, []);
  return (
    <div className="bg-white rounded-md border border-border divide-y divide-border">
      {items.length === 0 && <div className="p-8 text-center text-slate-400">No activity yet.</div>}
      {items.map((a) => (
        <div key={a.id} data-testid={`audit-${a.id}`} className="p-3 flex items-start gap-3">
          <div className="w-2 h-2 rounded-full bg-teal-500 mt-1.5" />
          <div className="flex-1"><div className="text-sm text-slate-800">{a.message}</div><div className="text-xs text-slate-400 font-num">{new Date(a.at).toLocaleString("en-AU")}</div></div>
        </div>
      ))}
    </div>
  );
}

function Accounts() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [editingLocked, setEditingLocked] = useState(false);
  const blank = { name: "", role: "supervisor", pin: "", phone: "", email: "", active: true };
  const [form, setForm] = useState(blank);
  const load = () => api.get("/admin/accounts").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const openNew = () => { setEditing(null); setConfirmDel(false); setEditingLocked(false); setForm(blank); setOpen(true); };
  const openEdit = (a) => { setEditing(a.id); setConfirmDel(false); setEditingLocked(a.locked); setForm({ name: a.name, role: a.role, pin: "", phone: a.phone || "", email: a.email || "", active: a.active }); setOpen(true); };
  const save = async () => {
    if (!form.name || !form.phone) return toast.error("Name and mobile number are required");
    if (form.pin && form.pin.length !== 4) return toast.error("PIN must be exactly 4 digits");
    try {
      if (editing) await api.put(`/admin/accounts/${editing}`, form);
      else await api.post("/admin/accounts", form);
      toast.success(editing ? "Account updated" : "Account created");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async () => {
    try { await api.delete(`/admin/accounts/${editing}`); toast.success("Account deleted"); setOpen(false); setConfirmDel(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const unlock = async () => {
    try { await api.post(`/admin/accounts/${editing}/unlock`); toast.success("Account unlocked"); setEditingLocked(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Unlock failed"); }
  };
  return (
    <div className="space-y-3">
      <Button data-testid="add-account" onClick={openNew} className="gap-1.5 btn-grad text-white rounded-full"><Plus size={16} strokeWidth={2.5} /> Add account</Button>
      <div className="bg-white rounded-md border border-border divide-y divide-border">
        {items.map((a) => (
          <button key={a.id} data-testid={`account-row-${a.id}`} onClick={() => openEdit(a)} className="w-full p-3 flex items-center justify-between text-left hover:bg-slate-50">
            <div>
              <div className="font-medium text-slate-800">{a.name}</div>
              <div className="text-xs text-slate-500 capitalize font-num">{a.role} · {a.phone || "no phone"}{a.locked ? " · 🔒 locked" : ""}</div>
            </div>
            <span className={`text-xs px-2 py-1 rounded-full ${a.active ? "bg-leaf text-slate-800" : "bg-slate-100 text-slate-500"}`}>{a.active ? "active" : "inactive"}</span>
          </button>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-slate-800">{editing ? "Edit account" : "New account"}</DialogTitle><DialogDescription className="text-xs">Set the name, sign-in number, role and PIN. Untick Active to deactivate.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="acct-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Mobile number (sign-in)</Label><Input data-testid="acct-phone" type="tel" inputMode="numeric" className="font-num" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>Email (optional)</Label><Input data-testid="acct-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="acct-role"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="supervisor">Supervisor</SelectItem><SelectItem value="admin">Admin / Manager</SelectItem><SelectItem value="superadmin">Super Admin</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>{editing ? "Reset PIN (leave blank to keep)" : "4-digit PIN (blank = last 4 of phone)"}</Label><Input data-testid="acct-pin" maxLength={4} value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "") })} className="font-num" /></div>
            {editing && <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" data-testid="acct-active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>}
          </div>
          <DialogFooter className="gap-2 sm:justify-between">
            {editing ? (
              <div className="flex items-center gap-2">
                {editingLocked && <Button data-testid="acct-unlock" variant="outline" onClick={unlock} className="text-amber-700 border-amber-300 rounded-xl">Unlock</Button>}
                {confirmDel
                  ? <Button data-testid="acct-delete-confirm" onClick={del} className="bg-rose-600 text-white rounded-xl">Confirm delete</Button>
                  : <Button data-testid="acct-delete" variant="outline" onClick={() => setConfirmDel(true)} className="text-rose-600 border-rose-300 rounded-xl">Delete</Button>}
              </div>
            ) : <span />}
            <Button data-testid="acct-save" onClick={save} className="btn-grad text-white">{editing ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Roster() {
  const [workers, setWorkers] = useState([]);
  const [products, setProducts] = useState([]);
  const [pForm, setPForm] = useState({ code: "", name: "", family: "" });
  const [pEdit, setPEdit] = useState(null);
  const [pEForm, setPEForm] = useState({ name: "", family: "" });
  const [bulk, setBulk] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [wEdit, setWEdit] = useState(null);   // worker being edited
  const [wForm, setWForm] = useState({ number: "", name: "", code: "" });
  const load = () => { api.get("/workers").then((r) => setWorkers(r.data)); api.get("/products").then((r) => setProducts(r.data)); };
  useEffect(() => { load(); }, []);
  const addP = async () => { if (!pForm.code) return; await api.post("/admin/products", { ...pForm, active: true }); setPForm({ code: "", name: "", family: "" }); load(); toast.success("Product added"); };
  const openPEdit = (p) => { setPEForm({ name: p.name || "", family: p.family || "" }); setPEdit(p); };
  const delP = async (p) => {
    if (!window.confirm(`Delete product ${p.code}${p.name && p.name !== p.code ? ` (${p.name})` : ""}? Past shifts keep their records.`)) return;
    try { await api.delete(`/admin/products/${p.id}`); load(); toast.success("Product deleted"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };
  const savePEdit = async () => { await api.put(`/admin/products/${pEdit.id}`, { code: pEdit.code, name: pEForm.name.trim(), family: pEForm.family.trim(), active: pEdit.active !== false }); setPEdit(null); load(); toast.success("Label updated"); };
  const bulkAdd = async () => {
    if (!bulk.trim()) return;
    const { data } = await api.post("/admin/workers/bulk", { text: bulk });
    toast.success(`Added ${data.added} worker${data.added === 1 ? "" : "s"}${data.skipped.length ? ` · skipped ${data.skipped.length}` : ""}`);
    if (data.skipped.length) toast.info("Skipped: " + data.skipped.slice(0, 6).join("; "));
    setBulk(""); load();
  };
  const openWEdit = (w) => { setWEdit(w); setWForm({ number: w.number ?? "", name: w.name || "", code: w.code || "" }); };
  const saveWEdit = async () => {
    if (!wForm.name.trim()) return toast.error("Name is required");
    await api.put(`/admin/workers/${wEdit.id}`, { name: wForm.name.trim(), code: wForm.code.trim(), number: wForm.number === "" ? null : Number(wForm.number), active: wEdit.active !== false });
    toast.success("Worker updated"); setWEdit(null); load();
  };
  const toggleActive = async (w) => {
    const next = w.active === false;
    await api.put(`/admin/workers/${w.id}`, { name: w.name, code: w.code || "", number: w.number ?? null, active: next });
    toast.success(next ? "Worker reactivated" : "Worker deactivated"); load();
  };
  const visible = [...workers].filter((w) => showInactive || w.active !== false).sort((a, b) => (a.number || 0) - (b.number || 0));
  const inactiveCount = workers.filter((w) => w.active === false).length;
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="bg-white rounded-md border border-border p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-bold text-slate-800">Workers ({workers.filter((w) => w.active !== false).length})</h3>
          {inactiveCount > 0 && (
            <label className="flex items-center gap-2 text-xs text-slate-500"><Switch data-testid="roster-show-inactive" checked={showInactive} onCheckedChange={setShowInactive} /> Show inactive ({inactiveCount})</label>
          )}
        </div>
        <div className="mb-3">
          <Label>Bulk add — one per line: <span className="font-num">no, name, contractorID</span></Label>
          <textarea data-testid="roster-bulk" rows={4} placeholder={"61, LI YaPing, C90050\n62, MASA UDI Geraldo Isa, C91181"} value={bulk} onChange={(e) => setBulk(e.target.value)}
            className="w-full mt-1 rounded-xl border border-slate-200 p-2 text-sm font-num focus:outline-none focus:ring-2 focus:ring-teal-400" />
          <Button data-testid="roster-bulk-add" onClick={bulkAdd} className="mt-2 btn-grad text-white rounded-full gap-1.5"><Plus size={16} strokeWidth={2.5} /> Bulk add</Button>
        </div>
        <div className="max-h-72 overflow-y-auto divide-y divide-border">
          {visible.map((w) => (
            <div key={w.id} data-testid={`roster-worker-${w.number}`} className={`py-2 flex items-center justify-between gap-2 ${w.active === false ? "opacity-50" : ""}`}>
              <span className="text-sm text-slate-800 truncate"><span className="font-num text-teal-600 font-semibold mr-2">#{w.number}</span>{w.name}{w.active === false && <span className="ml-2 text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">inactive</span>}</span>
              <div className="flex items-center gap-1 shrink-0">
                <span className="text-slate-400 font-num text-xs mr-1">{w.code}</span>
                <button data-testid={`roster-edit-${w.number}`} onClick={() => openWEdit(w)} className="p-1.5 text-slate-400 hover:text-teal-600" title="Edit"><Pencil size={15} strokeWidth={2.5} /></button>
                <button data-testid={`roster-toggle-${w.number}`} onClick={() => toggleActive(w)} className={`p-1.5 ${w.active === false ? "text-slate-400 hover:text-teal-600" : "text-slate-400 hover:text-rose-500"}`} title={w.active === false ? "Reactivate" : "Deactivate"}><Power size={15} strokeWidth={2.5} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-md border border-border p-4">
        <h3 className="font-heading font-bold text-slate-800 mb-3">Products ({products.length})</h3>
        <div className="flex gap-2 mb-3">
          <Input data-testid="product-code" placeholder="Code" className="w-24" value={pForm.code} onChange={(e) => setPForm({ ...pForm, code: e.target.value })} />
          <Input placeholder="Name" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} />
          <Button data-testid="add-product-roster" onClick={addP} className="btn-grad text-white"><Plus size={16} strokeWidth={2.5} /></Button>
        </div>
        <div className="max-h-72 overflow-y-auto divide-y divide-border">
          {products.map((p) => (
            <div key={p.id} data-testid={`product-row-${p.id}`} className="py-2 text-sm text-slate-800 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate font-medium text-slate-800">{p.name || p.code}</div>
                <div className="font-num text-[11px] text-slate-400 truncate">{p.code}{p.family ? ` · ${p.family}` : ""}</div>
              </div>
              <div className="flex shrink-0">
                <button data-testid={`product-edit-${p.id}`} onClick={() => openPEdit(p)} className="p-1 text-slate-400 hover:text-teal-600" title="Edit label"><Pencil size={14} strokeWidth={2.5} /></button>
                <button data-testid={`product-delete-${p.id}`} onClick={() => delP(p)} className="p-1 text-slate-400 hover:text-rose-600" title="Delete product"><Trash2 size={14} strokeWidth={2.5} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <Dialog open={!!pEdit} onOpenChange={(o) => { if (!o) setPEdit(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-slate-800">Edit product label</DialogTitle><DialogDescription className="text-xs">The code <span className="font-num font-semibold">{pEdit?.code}</span> is locked (it links to historical shifts). Update the label and family.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Label / name</Label><Input data-testid="pedit-name" value={pEForm.name} onChange={(e) => setPEForm({ ...pEForm, name: e.target.value })} placeholder="e.g. Frames on tray 15kg" /></div>
            <div><Label>Family</Label><Input data-testid="pedit-family" value={pEForm.family} onChange={(e) => setPEForm({ ...pEForm, family: e.target.value })} /></div>
          </div>
          <DialogFooter><Button data-testid="pedit-save" onClick={savePEdit} className="btn-grad text-white">Save label</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!wEdit} onOpenChange={(o) => { if (!o) setWEdit(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-slate-800">Edit worker</DialogTitle><DialogDescription className="text-xs">Update the roster number, name or contractor code.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Roster number</Label><Input data-testid="wedit-number" type="number" inputMode="numeric" className="font-num" value={wForm.number} onChange={(e) => setWForm({ ...wForm, number: e.target.value })} /></div>
            <div><Label>Name</Label><Input data-testid="wedit-name" value={wForm.name} onChange={(e) => setWForm({ ...wForm, name: e.target.value })} /></div>
            <div><Label>Contractor code</Label><Input data-testid="wedit-code" className="font-num" value={wForm.code} onChange={(e) => setWForm({ ...wForm, code: e.target.value })} /></div>
          </div>
          <DialogFooter><Button data-testid="wedit-save" onClick={saveWEdit} className="btn-grad text-white">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PwFormula() {
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/meta").then((r) => setF(r.data.pw_formula)); }, []);
  if (!f) return null;
  const set = (k, field, v) => setF({ ...f, [k]: { ...f[k], [field]: v } });
  const save = async () => {
    try { await api.put("/pw-formula", f); toast.success("Portion Wing formula saved"); } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  const rows = [["a", "Weekday 1st shift", "total minutes ÷ 60"], ["sat", "Saturday", "total hours"], ["sunph", "Sunday & public holiday", "total hours"]];
  return (
    <div className="bg-white rounded-xl border border-border p-3 space-y-2" data-testid="pw-formula">
      <div className="text-sm font-semibold text-slate-700">Portion Wing line: kilos from minutes</div>
      <p className="text-xs text-slate-500">Kilos = hours × rate ÷ divisor, split between the crew by minutes. Other weekday shifts stay weighed.</p>
      {rows.map(([k, l, h]) => (
        <div key={k} className="flex flex-wrap items-center gap-2 text-sm">
          <span className="w-44 text-slate-700">{l}</span><span className="text-slate-400 text-xs">{h} ×</span>
          <Input data-testid={`pw-${k}-rate`} type="number" value={f[k]?.rate ?? ""} onChange={(e) => set(k, "rate", e.target.value)} className="w-20 h-9 font-num" />
          <span className="text-slate-400 text-xs">÷</span>
          <Input data-testid={`pw-${k}-div`} type="number" value={f[k]?.div ?? ""} onChange={(e) => set(k, "div", e.target.value)} className="w-20 h-9 font-num" />
        </div>
      ))}
      <Button data-testid="pw-save" onClick={save} className="h-9 rounded-full btn-grad text-white text-xs gap-1"><Save size={14} /> Save formula</Button>
    </div>
  );
}

function Pricing() {
  const [lines, setLines] = useState([]);
  const [rates, setRates] = useState({});
  const [dirty, setDirty] = useState(false);
  const load = () => api.get("/line-rates").then((r) => { setLines(r.data.lines || []); setRates(r.data.rates || {}); setDirty(false); });
  useEffect(() => { load(); }, []);
  const setCell = (line, cat, val) => { setRates((p) => ({ ...p, [line]: { ...(p[line] || { a: 0, b: 0, c: 0 }), [cat]: val } })); setDirty(true); };
  const save = async () => {
    const payload = {}; lines.forEach((l) => { payload[l] = { a: Number(rates[l]?.a) || 0, b: Number(rates[l]?.b) || 0, c: Number(rates[l]?.c) || 0 }; });
    await api.put("/line-rates", { rates: payload });
    toast.success("Line pricing saved"); load();
  };
  return (
    <div className="space-y-3">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
        Pricing is <b>by line</b> ($/kg), not by product. <b>Column A</b> = 1st (Night) shift on weekdays. <b>Column B</b> = 2nd, 3rd shift & Pooraka Day. <b>Column C</b> = Saturday, Sunday & Public Holidays. Product <b>W7324</b> kilos are excluded from the payable value. Admin only.
      </div>
      <PwFormula />
      <div className="flex justify-end">
        <Button data-testid="pricing-save" onClick={save} disabled={!dirty} className="gap-1.5 btn-grad text-white rounded-full"><Save size={16} strokeWidth={2.5} /> Save line pricing</Button>
      </div>
      <div className="bg-white rounded-md border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-slate-500 text-xs"><th className="px-3 py-2">Line</th><th className="px-2 py-2">A: 1st (Night) $/kg</th><th className="px-2 py-2">B: 2nd / 3rd / Pooraka $/kg</th><th className="px-2 py-2">C: Sat / Sun / PH $/kg</th></tr></thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l} data-testid={`line-row-${l}`} className="border-t border-border">
                <td className="px-3 py-2 font-num text-slate-800 whitespace-nowrap font-semibold">{l}</td>
                {["a", "b", "c"].map((cat) => (
                  <td key={cat} className="px-2 py-1.5"><Input type="number" step="0.01" data-testid={`line-${l}-${cat}`} className="h-12 w-24 font-num text-base" placeholder="—" value={rates[l]?.[cat] ?? ""} onChange={(e) => setCell(l, cat, e.target.value)} /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  const [holInput, setHolInput] = useState("");
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="text-slate-400 p-4">Loading…</div>;
  const setBw = (i, v) => setS({ ...s, bucket_weights: s.bucket_weights.map((x, idx) => (idx === i ? Number(v) : x)) });
  const setRt = (i, v) => setS({ ...s, reminder_times: (s.reminder_times || []).map((x, idx) => (idx === i ? v : x)) });
  const holidays = (s.holidays || []).slice().sort();
  const addHoliday = () => { if (!holInput) return; if (holidays.includes(holInput)) return toast.info("Already added"); setS({ ...s, holidays: [...holidays, holInput].sort() }); setHolInput(""); };
  const delHoliday = (d) => setS({ ...s, holidays: holidays.filter((x) => x !== d) });
  const fmtHol = (d) => new Date(d + "T12:00:00").toLocaleDateString("en-AU", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
  const save = async () => {
    await api.put("/admin/settings", { bucket_weights: s.bucket_weights, week_anchor: s.week_anchor, holidays: s.holidays || [], shifts: s.shifts || [], reminder_times: s.reminder_times || [], telegram_group_id: s.telegram_group_id || "", app_url: s.app_url || "", reminders_enabled: !!s.reminders_enabled });
    toast.success("Settings saved");
  };
  return (
    <div className="space-y-4 max-w-2xl">
      <div className="bg-white rounded-md border border-border p-4">
        <h3 className="font-heading font-bold text-slate-800 mb-3">Bucket kg by weekday</h3>
        <div className="grid grid-cols-7 gap-2">
          {DAYS.map((d, i) => (
            <div key={d} className="text-center"><div className="text-xs text-slate-500 mb-1">{d}</div>
              <Input data-testid={`bucket-${i}`} type="number" className="h-11 text-center font-num" value={s.bucket_weights[i]} onChange={(e) => setBw(i, e.target.value)} /></div>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-md border border-border p-4 space-y-3">
        <h3 className="font-heading font-bold text-slate-800">Week anchor & reminders</h3>
        <div><Label>Week 1 Sunday (anchor)</Label><Input data-testid="week-anchor" type="date" className="h-11" value={s.week_anchor || ""} onChange={(e) => setS({ ...s, week_anchor: e.target.value })} /></div>
        <div><Label>Reminder times — 1st (Night) · 2nd (Day) · 3rd (Afternoon)</Label>
          <div className="grid grid-cols-3 gap-2 mt-1">{(s.reminder_times || ["06:30", "14:30", "22:30"]).map((t, i) => <Input key={i} data-testid={`reminder-${i}`} type="time" className="h-11 font-num" value={t} onChange={(e) => setRt(i, e.target.value)} />)}</div>
          <p className="text-xs text-slate-400 mt-1">Set each ~60 min after that shift ends. If no kilos are logged for a shift by its time, linked supervisors get a Telegram nudge.</p>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700"><Switch data-testid="reminders-enabled" checked={!!s.reminders_enabled} onCheckedChange={(v) => setS({ ...s, reminders_enabled: v })} /> Send Telegram reminders</label>
        <div><Label>App link (included in the reminder)</Label><Input data-testid="app-url" placeholder="https://ready2work.com.au" value={s.app_url || ""} onChange={(e) => setS({ ...s, app_url: e.target.value })} /></div>
      </div>
      <TelegramCard />
      <SmsCard />
      <div className="bg-white rounded-md border border-border p-4 space-y-3">
        <h3 className="font-heading font-bold text-slate-800">Public holidays (SA)</h3>
        <p className="text-xs text-slate-500">Holiday rates in Pricing only apply on these dates. Add or remove public holidays as they change year to year.</p>
        <div className="flex gap-2">
          <Input data-testid="holiday-input" type="date" className="h-11 flex-1" value={holInput} onChange={(e) => setHolInput(e.target.value)} />
          <Button data-testid="holiday-add" onClick={addHoliday} className="btn-grad text-white gap-1.5"><Plus size={16} strokeWidth={2.5} /> Add</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {holidays.length === 0 && <span className="text-sm text-slate-400">No holidays set.</span>}
          {holidays.map((d) => (
            <span key={d} data-testid={`holiday-chip-${d}`} className="inline-flex items-center gap-1.5 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-xs font-semibold px-3 py-1.5">
              {fmtHol(d)}
              <button data-testid={`holiday-del-${d}`} onClick={() => delHoliday(d)} className="text-teal-500 hover:text-rose-500"><X size={13} strokeWidth={3} /></button>
            </span>
          ))}
        </div>
      </div>
      <Button data-testid="settings-save" onClick={save} className="gap-1.5 btn-grad text-white rounded-full"><Save size={16} strokeWidth={2.5} /> Save settings</Button>
    </div>
  );
}


function TelegramCard() {
  const [status, setStatus] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const load = () => { api.get("/admin/telegram/status").then((r) => setStatus(r.data)); api.get("/admin/accounts").then((r) => setAccounts(r.data)); };
  useEffect(() => { load(); }, []);
  const refresh = async () => {
    setBusy(true);
    try { const { data } = await api.get("/admin/telegram/contacts"); setContacts(data); if (!data.length) toast.info("No messages yet — ask each supervisor to open the bot and tap Start, then Refresh."); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not fetch contacts"); }
    setBusy(false);
  };
  const linkedChatIds = new Set((status?.linked || []).map((l) => String(l.chat_id)));
  const link = async (c) => { const account_id = sel[c.chat_id]; if (!account_id) return toast.error("Pick a supervisor for this contact"); await api.post("/admin/telegram/link", { account_id, chat_id: c.chat_id, name: c.name }); toast.success(`Linked ${c.name}`); load(); };
  const unlink = async (id) => { await api.post("/admin/telegram/unlink", { account_id: id }); toast.success("Unlinked"); load(); };
  const test = async (id) => { try { await api.post("/admin/telegram/test", { account_id: id }); toast.success("Test message sent ✓"); } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); } };
  const pending = (contacts || []).filter((c) => !linkedChatIds.has(String(c.chat_id)));
  return (
    <div className="bg-white rounded-md border border-border p-4 space-y-3" data-testid="telegram-card">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-bold text-slate-800">Telegram supervisors</h3>
        {status && (status.configured
          ? <span className="text-xs px-2 py-1 rounded-full bg-teal-50 text-teal-700 font-semibold">Bot {status.bot_username ? "@" + status.bot_username : "connected"}</span>
          : <span className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-700 font-semibold">Token not set</span>)}
      </div>
      <p className="text-xs text-slate-500">Ask each supervisor to open the bot{status?.bot_username ? ` (t.me/${status.bot_username})` : ""} and tap <b>Start</b>. Then Refresh and link them to their account.</p>
      <Button data-testid="tg-refresh" onClick={refresh} disabled={busy || !status?.configured} variant="outline" className="gap-1.5 rounded-full">{busy ? "Checking…" : "Refresh contacts"}</Button>
      {pending.length > 0 && (
        <div className="space-y-2">
          {pending.map((c) => (
            <div key={c.chat_id} data-testid={`tg-contact-${c.chat_id}`} className="flex items-center gap-2 border border-slate-100 rounded-xl p-2">
              <div className="flex-1 min-w-0"><div className="text-sm text-slate-800 truncate">{c.name}</div><div className="text-xs text-slate-400 font-num">{c.username ? "@" + c.username + " · " : ""}{c.chat_id}</div></div>
              <Select value={sel[c.chat_id] || ""} onValueChange={(v) => setSel({ ...sel, [c.chat_id]: v })}>
                <SelectTrigger data-testid={`tg-assign-${c.chat_id}`} className="h-10 w-40 rounded-xl text-xs"><SelectValue placeholder="Assign to…" /></SelectTrigger>
                <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.role})</SelectItem>)}</SelectContent>
              </Select>
              <Button data-testid={`tg-link-${c.chat_id}`} onClick={() => link(c)} className="h-10 btn-grad text-white rounded-xl text-xs">Link</Button>
            </div>
          ))}
        </div>
      )}
      <div className="divide-y divide-border border-t border-border pt-1">
        <div className="text-xs font-semibold text-slate-500 py-1">Linked ({status?.linked?.length || 0})</div>
        {(status?.linked || []).length === 0 && <div className="text-xs text-slate-400 py-1">No supervisors linked yet.</div>}
        {(status?.linked || []).map((l) => (
          <div key={l.account_id} data-testid={`tg-linked-${l.account_id}`} className="flex items-center gap-2 py-2">
            <div className="flex-1 min-w-0"><div className="text-sm text-slate-800 truncate">{l.name} <span className="text-xs text-slate-400 capitalize">· {l.role}</span></div><div className="text-xs text-slate-400 font-num">{l.tg_name ? l.tg_name + " · " : ""}{l.chat_id}</div></div>
            <Button data-testid={`tg-test-${l.account_id}`} onClick={() => test(l.account_id)} variant="outline" className="h-9 rounded-xl text-xs">Test</Button>
            <Button data-testid={`tg-unlink-${l.account_id}`} onClick={() => unlink(l.account_id)} variant="outline" className="h-9 rounded-xl text-xs text-rose-600 border-rose-200">Unlink</Button>
          </div>
        ))}
      </div>
    </div>
  );
}


function SmsCard() {
  const [accounts, setAccounts] = useState([]);
  const [sel, setSel] = useState({});
  const [msg, setMsg] = useState("");
  const [appUrl, setAppUrl] = useState("");
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const loadLog = () => api.get("/admin/sms/log", { params: { limit: 100 } }).then((r) => setLog(r.data));
  useEffect(() => {
    api.get("/admin/accounts").then((r) => setAccounts(r.data.filter((a) => a.phone)));
    api.get("/settings").then((r) => setAppUrl(r.data.app_url || window.location.origin));
    loadLog();
  }, []);
  const link = appUrl || window.location.origin;
  const templates = [
    { label: "Finish your log", text: `Hi 🙂 when you get a moment, please finish your production log so payroll stays spot on: ${link}` },
    { label: "Scales photo missing", text: `Hey! 📷 The photo of the scales is missing on your last log — could you pop it in the app? Thanks! ${link}` },
    { label: "Log looks unbalanced", text: `Hi 👋 your last log looks a little unbalanced ⚖️ — mind checking the kilos when you're free? ${link}` },
    { label: "Call me", text: `Hi 🙂 give me a quick call when you get a sec — cheers!` },
  ];
  const chosen = accounts.filter((a) => sel[a.id]);
  const toggle = (id) => setSel((s) => ({ ...s, [id]: !s[id] }));
  const send = async () => {
    if (!msg.trim()) return toast.error("Type a message first");
    if (chosen.length === 0) return toast.error("Pick at least one supervisor");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/sms/send", { account_ids: chosen.map((a) => a.id), body: msg.trim() });
      const fails = data.results.filter((r) => !r.ok);
      if (fails.length === 0) toast.success(`Sent to ${data.sent} supervisor${data.sent === 1 ? "" : "s"} ✓`);
      else toast.warning(`Sent ${data.sent}/${data.total}. Failed: ${fails.map((f) => f.name).join(", ")}`);
      setMsg(""); setSel({}); loadLog();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    setBusy(false);
  };
  const refresh = async () => {
    setRefreshing(true);
    try { const { data } = await api.post("/admin/sms/refresh"); await loadLog(); toast.success(`Refreshed — ${data.updated} updated`); }
    catch (e) { toast.error(e.response?.data?.detail || "Refresh failed"); }
    setRefreshing(false);
  };
  const badge = (s) => ({ delivered: "bg-green-100 text-green-700", sent: "bg-amber-100 text-amber-700", queued: "bg-amber-100 text-amber-700", accepted: "bg-amber-100 text-amber-700", failed: "bg-rose-100 text-rose-700", undelivered: "bg-rose-100 text-rose-700" }[s] || "bg-slate-100 text-slate-600");
  const segs = msg.length ? Math.ceil(msg.length / 160) : 0;
  return (
    <div className="bg-white rounded-md border border-border p-4 space-y-3" data-testid="sms-card">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-bold text-slate-800">Text supervisors (SMS)</h3>
        <span className="text-xs px-2 py-1 rounded-full bg-teal-50 text-teal-700 font-semibold">Sender: Ready2Work</span>
      </div>
      <p className="text-xs text-slate-500">Send a personalised text to one or more supervisors. Automated reminders also appear in the log below.</p>

      <div className="flex flex-wrap gap-1.5">
        {templates.map((t, i) => (
          <button key={i} data-testid={`sms-template-${i}`} onClick={() => setMsg(t.text)} className="text-xs font-semibold rounded-full border border-slate-200 bg-slate-50 hover:bg-teal-50 hover:border-teal-200 text-slate-600 px-2.5 py-1.5 transition-colors">{t.label}</button>
        ))}
      </div>

      <div>
        <Textarea data-testid="sms-message" value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} placeholder="Type your message… (tap a template above to start)" className="text-sm" />
        <div className="text-[11px] text-slate-400 mt-1 font-num">{msg.length} chars · {segs} SMS segment{segs === 1 ? "" : "s"}</div>
      </div>

      <div>
        <div className="text-xs font-semibold text-slate-500 mb-1.5">Send to ({chosen.length} selected)</div>
        <div className="space-y-1.5 max-h-56 overflow-y-auto">
          {accounts.length === 0 && <div className="text-xs text-slate-400">No accounts with a mobile number yet.</div>}
          {accounts.map((a) => (
            <label key={a.id} data-testid={`sms-recipient-${a.id}`} className="flex items-center gap-2 border border-slate-100 rounded-xl p-2 cursor-pointer select-none hover:bg-slate-50">
              <input type="checkbox" className="w-4 h-4 accent-teal-600" checked={!!sel[a.id]} onChange={() => toggle(a.id)} />
              <div className="flex-1 min-w-0"><div className="text-sm text-slate-800 truncate">{a.name} <span className="text-xs text-slate-400 capitalize">· {a.role}</span></div><div className="text-xs text-slate-400 font-num">{a.phone}</div></div>
            </label>
          ))}
        </div>
      </div>

      <Button data-testid="sms-send" onClick={send} disabled={busy} className="gap-1.5 btn-grad text-white rounded-full">{busy ? "Sending…" : `Send SMS${chosen.length ? ` (${chosen.length})` : ""}`}</Button>

      <div className="border-t border-border pt-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold text-slate-500">SMS log ({log.length})</div>
          <Button data-testid="sms-refresh" onClick={refresh} disabled={refreshing} variant="outline" className="h-8 rounded-full text-xs">{refreshing ? "Refreshing…" : "Refresh status"}</Button>
        </div>
        <div className="space-y-1.5 max-h-80 overflow-y-auto">
          {log.length === 0 && <div className="text-xs text-slate-400">No SMS sent yet.</div>}
          {log.map((r) => (
            <div key={r.id} data-testid={`sms-log-row-${r.id}`} className="border border-slate-100 rounded-xl p-2.5">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-semibold text-slate-800 truncate">{r.to_name || r.to || "—"}</span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${r.kind === "manual" ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-500"}`}>{r.kind === "manual" ? "Manual" : "Auto"}</span>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full capitalize whitespace-nowrap ${badge(r.status)}`}>{r.status || "—"}</span>
              </div>
              <div className="text-[11px] text-slate-400 font-num mt-0.5">{r.at ? new Date(r.at).toLocaleString("en-AU") : ""}{r.sent_by_name ? ` · by ${r.sent_by_name}` : ""}</div>
              <button onClick={() => setExpanded(expanded === r.id ? null : r.id)} className="text-xs text-slate-600 mt-1 text-left w-full">
                <span className={expanded === r.id ? "" : "line-clamp-1"}>{r.body}</span>
              </button>
              {r.error && <div className="text-[11px] text-rose-500 mt-0.5">⚠ {r.error}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
